/**
 * Frontend runtime configuration.
 *
 * The Worker (backend API) origin is supplied at build time via the
 * `VITE_WORKER_API_URL` environment variable (set in the Pages dashboard or a
 * local `.env`). It is NEVER hardcoded to localhost in source — the value
 * always comes from the build environment, so a production build always targets
 * the real, deployed Worker.
 */
export const WORKER_API_URL = (import.meta.env.VITE_WORKER_API_URL || '').replace(/\/+$/, '');

/**
 * Verified public URL of the existing NallaSMP server-management panel.
 *
 * Discovered (not guessed) by inspecting the sibling NallaSMP panel project at
 * `/opt/nallasmp-panel` and its runtime config:
 *   - README: hostname `panel.nallasmp.duckdns.org`
 *   - /etc/caddy/Caddyfile: `panel.nallasmp.duckdns.org` -> 127.0.0.1:3000
 *   - Live check: HTTPS 200, title "NallaSMP Control Panel — Minecraft Server
 *     Dashboard" on /login.
 *   - Auth KDF (scrypt N=16384) matches the Worker's comment referencing
 *     "the existing panel".
 * Both panel.nallasmp.duckdns.org and the alias nallasmp.duckdns.org resolve to
 * the same AWS ap-south-1 host. This is the canonical destination.
 */
export const DEFAULT_NALLASMP_PANEL_URL = 'https://panel.nallasmp.duckdns.org';

/**
 * Destination for the dashboard's "Go to NallaSMP Panel" button.
 *
 * Defaults to the verified panel URL above. Override at build time with the
 * `VITE_NALLASMP_PANEL_URL` environment variable (e.g. for a staging panel).
 * It is NEVER silently pointed at this Cloudflare control panel itself.
 */
export const NALLASMP_PANEL_URL = (import.meta.env.VITE_NALLASMP_PANEL_URL || DEFAULT_NALLASMP_PANEL_URL).replace(/\/+$/, '');

/**
 * Path on the NallaSMP panel where SSO handoff tokens are redeemed.
 * The Worker mints the token; the frontend POSTs it here in a new tab.
 */
export const SSO_REDEEM_PATH = '/api/sso/redeem';

/** How often the panel polls GET /api/status while an action is in flight. */
export const POLL_INTERVAL_MS = 4_000;
/** Give an EC2 state transition at most this long before reporting a timeout. */
export const ACTION_TIMEOUT_MS = 90_000;

export type ApiErrorCode =
  | 'NO_CREDENTIALS' | 'TIMEOUT' | 'THROTTLED' | 'UNAUTHORIZED'
  | 'INSTANCE_NOT_FOUND' | 'AWS_ERROR'
  | 'INVALID_STATE' | 'DUPLICATE' | 'TERMINATED';

/**
 * Maps a server-side `errorCode` to a user-friendly, non-sensitive message.
 * AWS-internal details are never surfaced; sensitive codes use a fixed string,
 * while INVALID_STATE/DUPLICATE/TERMINATED carry their own server message
 * (passed as `fallback`) which is safe to display.
 */
export function friendlyError(
  code: string | undefined,
  fallback = 'An unexpected error occurred. Please try again.',
): string {
  switch (code) {
    case 'NO_CREDENTIALS': return 'The control backend is not configured.';
    case 'TIMEOUT': return 'The request timed out. Please try again.';
    case 'THROTTLED': return 'Too many requests. Please wait and try again.';
    case 'UNAUTHORIZED': return 'Session expired. Please log in again.';
    case 'INSTANCE_NOT_FOUND': return 'Instance not found.';
    default:
      return fallback;
  }
}

export interface AuthState {
  user: string;
  role: 'admin' | 'viewer';
  csrfToken: string;
}
